import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Divider,
  Alert,
  Skeleton,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { format, parseISO, addMonths, addYears, addWeeks, addDays, subDays } from 'date-fns'
import {
  changePlanDialogStyles,
  getPlanOptionCardSx,
  getPillSx,
  getPriceDeltaSpanSx,
  PillTone,
} from './change-plan-dialog.styles'

import DialogLayout from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.component'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import {
  CLOSE_TYPES,
  SIZES,
} from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import SharedButton from '@hapana/shared/web/components/shared/button/button.component'
import { useMutation, resolve, Mutation } from '@hapana/shared/web/services/gqty'
import { localTimeFormat } from '@hapana/shared/utils/date.util'
import { getCurrencySymbol } from '@hapana/shared/utils/string.util'
import useSignatureCaptureStore from '_stores/signature-capture.store'
import { useGetSiteById } from '_hooks/gqty/sites.hooks'
import { useMe } from '_hooks/me.hooks'

import {
  useAvailablePlanOptions,
  useCalculateCart,
  useChangePlanAgreement,
  PlanOption,
  AgreementRequirement,
} from './use-change-plan.hooks'
import { normalizeIsoDate } from '../../hooks/client-packages.utils'
import SignAgreementDialog from '../../../documents/dialogs/sign-agreement-dialog.component'

type EffectiveType = 'Immediate' | 'EndOfCycle'
type Step = 1 | 2 | 3 | 4

// The dialog stack renders only the top dialog (unmountOnExit). When SignAgreementDialog
// is pushed, ChangePlanDialog unmounts and loses its React state. This snapshot bridges
// the two dialog levels so state is restored on remount. It is keyed by serviceId so a
// snapshot from one service's dialog is never restored into another's.
interface ChangePlanSnapshot {
  serviceId: string
  step: Step
  selectedPlan: PlanOption | null
  effectiveType: EffectiveType
  reason: string
  signedClientAgreementId: string | null
}
let _snapshot: ChangePlanSnapshot | null = null

interface ChangePlanDialogProps {
  clientId: string
  siteId: string
  serviceId: string
  serviceName: string
  currentPrice: number
  currency: string
  nextInvoiceDate?: string
  siteSessionTypeId?: string
  onChangePlan: (data: ChangePlanData) => Promise<void>
}

export interface ChangePlanData {
  toServiceTemplateId: string
  effectiveType: EffectiveType
  reason?: string
  signedClientAgreementId?: string
}

const DIRECTION_PILL_TONE: Record<PlanOption['direction'], PillTone> = {
  UPGRADE: 'INFO',
  DOWNGRADE: 'DOWNGRADE',
  CURRENT: 'NEUTRAL',
}

function formatDate(dateStr: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(normalizeIsoDate(dateStr)))
}

function formatCurrency(amount: number | string, locale: string, currency: string): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(num)
}

interface SiteShortcodeData {
  name?: string | null
  phone?: string | null
  businessEmail?: string | null
  website?: string | null
  addressStreet?: string | null
  addressStreet2?: string | null
  addressCity?: string | null
  addressState?: string | null
  addressPostalCode?: string | null
  timezone?: string | null
  logoImage?: { url?: string | null } | null
  settings?: {
    location?: {
      configurations?: { currency?: string | null; timeFormat?: string | null } | null
    } | null
  } | null
}

function formatAgreementDate(isoStr: string | null | undefined): string | null {
  if (!isoStr) return null
  const datePart = String(isoStr).split('T')[0] ?? String(isoStr)
  try {
    return format(parseISO(datePart), 'd MMM yyyy')
  } catch {
    return datePart
  }
}

function computePackageShortcodes(
  agreementReq: AgreementRequirement,
  {
    t,
    locale,
    currency,
    nextInvoiceDate,
    siteData,
    effectiveType,
    salesperson,
  }: {
    t: (key: string) => string
    locale: string
    currency: string
    nextInvoiceDate?: string | null
    siteData?: SiteShortcodeData | null
    effectiveType?: 'Immediate' | 'EndOfCycle'
    salesperson?: string | null
  }
): Record<string, string | null> {
  const billingCycle =
    agreementReq.billingCycleAmount && agreementReq.billingCycleUnit
      ? `${agreementReq.billingCycleAmount} ${agreementReq.billingCycleUnit}`
      : null

  const membershipLength =
    agreementReq.lengthAmount && agreementReq.lengthUnit
      ? `${agreementReq.lengthAmount} ${agreementReq.lengthUnit}`
      : t('retailDashboard.recurringPaymentDetail.ongoing')

  const secondaryFeeBillingCycle =
    agreementReq.secondaryFeeEnabled &&
    agreementReq.secondaryFeeBillingCycleAmount &&
    agreementReq.secondaryFeeBillingCycleUnit
      ? `${agreementReq.secondaryFeeBillingCycleAmount} ${agreementReq.secondaryFeeBillingCycleUnit}`
      : null

  const firstBillingStartDate: Date =
    effectiveType === 'Immediate' || !nextInvoiceDate
      ? new Date()
      : new Date(normalizeIsoDate(nextInvoiceDate))

  const firstBillingDate = formatAgreementDate(firstBillingStartDate.toISOString())

  const expDate = (() => {
    if (!agreementReq.lengthAmount || !agreementReq.lengthUnit) return null
    let expiry: Date
    const unit = agreementReq.lengthUnit.toUpperCase()
    const amount = agreementReq.lengthAmount
    if (unit === 'YEARS') expiry = addYears(firstBillingStartDate, amount)
    else if (unit === 'MONTHS') expiry = addMonths(firstBillingStartDate, amount)
    else if (unit === 'WEEKS') expiry = addWeeks(firstBillingStartDate, amount)
    else expiry = addDays(firstBillingStartDate, amount)
    return formatAgreementDate(subDays(expiry, 1).toISOString())
  })()

  const firstSecondaryFeeBillingDate = (() => {
    if (agreementReq.type !== 'MEMBERSHIP' || !agreementReq.secondaryFeeEnabled) return null
    const { secondaryFeeFirstPaymentDateEnabled, secondaryFeeFirstPaymentDateOnDate } = agreementReq
    if (!secondaryFeeFirstPaymentDateEnabled) return firstBillingDate
    if (secondaryFeeFirstPaymentDateOnDate) {
      return formatAgreementDate(secondaryFeeFirstPaymentDateOnDate)
    }
    return firstBillingDate
  })()

  const sharedLocations = (() => {
    if (agreementReq.isAllSharedSites) {
      return t('settings.retail.packages.form.locationPermission.allLocations')
    }
    if (!agreementReq.sharedLocationNames?.length) return null
    return agreementReq.sharedLocationNames.join(', ')
  })()

  const credits = (() => {
    if (!agreementReq.credits?.length) return null
    const namesMap = agreementReq.sessionTypeNamesMap ?? {}
    return agreementReq.credits
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((credit) => {
        const amount =
          credit.creditsType === 'UNLIMITED'
            ? t('retailDashboard.packages.unlimited')
            : String(credit.amount ?? 0)
        const sessionNames = (credit.siteSessionTypeIds ?? [])
          .map((id) => namesMap[id])
          .filter(Boolean)
          .join(' / ')
        return sessionNames ? `${amount} ${sessionNames}` : amount
      })
      .join(', ')
  })()

  const siteAddress = siteData
    ? [
        siteData.addressStreet,
        siteData.addressStreet2,
        siteData.addressCity,
        siteData.addressState,
        siteData.addressPostalCode,
      ]
        .filter(Boolean)
        .join(', ') || null
    : null

  const siteTimezone = siteData?.timezone
  const siteCurrencyCode = siteData?.settings?.location?.configurations?.currency
  const siteTimeFormat = siteData?.settings?.location?.configurations?.timeFormat

  return {
    TODAY_DATE: new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date()),
    PACKAGE_NAME: agreementReq.name,
    MEMBERSHIP_NAME: agreementReq.name,
    PACKAGE_DESCRIPTION: agreementReq.description,
    BILLING_CYCLE: billingCycle,
    AUTO_RENEW: agreementReq.autoRenewEnabled ? t('common:yes') : t('common:no'),
    AUTO_RENEW_PACKAGE: agreementReq.autoRenewPackageName ?? null,
    AMOUNT: agreementReq.price != null ? String(agreementReq.price) : null,
    SETUP_FEE:
      agreementReq.oneTimeSetupFee != null
        ? formatCurrency(agreementReq.oneTimeSetupFee, locale, currency)
        : null,
    MEMBERSHIP_LENGTH: membershipLength,
    MEMBERSHIP_CANCELLATION_CHARGE:
      agreementReq.cancellationCharge != null
        ? formatCurrency(agreementReq.cancellationCharge, locale, currency)
        : null,
    MINIMUM_CANCELLATION_TERM:
      agreementReq.minimumTermAmount != null && agreementReq.minimumTermUnit != null
        ? `${agreementReq.minimumTermAmount} ${agreementReq.minimumTermUnit}`
        : null,
    SECONDARY_FEE_AMOUNT:
      agreementReq.secondaryFeeEnabled && agreementReq.secondaryFeeAmount != null
        ? formatCurrency(agreementReq.secondaryFeeAmount, locale, currency)
        : null,
    SECONDARY_FEE_BILLING_CYCLE: secondaryFeeBillingCycle,
    FIRST_BILLING_DATE: firstBillingDate,
    FIRST_CREDIT_ALLOCATION_DATE: firstBillingDate,
    FIRST_SECONDARY_FEE_BILLING_DATE: firstSecondaryFeeBillingDate,
    EXP_DATE: expDate,
    CREDITS: credits,
    SHARED_BUSINESS_LOCATIONS: sharedLocations,
    DISCOUNTED_AMOUNT: '0', // plan changes have no discounts
    SALES_TAX: (() => {
      if (!agreementReq.taxEnabled || !agreementReq.taxRates.length || !agreementReq.price)
        return '0'
      const price = parseFloat(String(agreementReq.price))
      if (isNaN(price)) return '0'
      const exclusiveTax = agreementReq.taxRates
        .filter((r) => r.setting === 'exclusive')
        .reduce((sum, r) => {
          const rate = parseFloat(r.rate)
          return isNaN(rate) ? sum : sum + (price * rate) / 100
        }, 0)
      return String(Math.round(exclusiveTax * 100) / 100)
    })(),
    SALESPERSON: salesperson ?? null,
    BUSINESS_NAME: siteData?.name ?? null,
    BUSINESS_PHONE: siteData?.phone ?? null,
    BUSINESS_EMAIL: siteData?.businessEmail ?? null,
    BUSINESS_WEBSITE: siteData?.website ?? null,
    SITE_ADDRESS: siteAddress,
    CURRENCY_SYMBOL: siteCurrencyCode ? getCurrencySymbol(siteCurrencyCode) : null,
    LOCAL_TIME: siteTimezone
      ? localTimeFormat(new Date(), siteTimezone, siteTimeFormat ?? undefined)
      : null,
  }
}

function renderAgreementText(
  text: string,
  {
    agreementReq,
    t,
    locale,
    currency,
    nextInvoiceDate,
    siteData,
    effectiveType,
    salesperson,
  }: {
    agreementReq: AgreementRequirement
    t: (key: string) => string
    locale: string
    currency: string
    nextInvoiceDate?: string | null
    siteData?: SiteShortcodeData | null
    effectiveType?: 'Immediate' | 'EndOfCycle'
    salesperson?: string | null
  }
): string {
  const shortcodeValues = computePackageShortcodes(agreementReq, {
    t,
    locale,
    currency,
    nextInvoiceDate,
    siteData,
    effectiveType,
    salesperson,
  })

  const escapeHtml = (value: string) =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')

  let result = text
  for (const [key, value] of Object.entries(shortcodeValues)) {
    if (value) {
      const escaped = escapeHtml(String(value))
      result = result.replaceAll(`&lt;${key}&gt;`, escaped).replaceAll(`<${key}>`, escaped)
    }
  }

  return result
}

function Pill({ tone, children }: { tone: PillTone; children: React.ReactNode }) {
  return (
    <Box component="span" sx={getPillSx(tone)}>
      {children}
    </Box>
  )
}

function DirectionBadge({ direction }: { direction: PlanOption['direction'] }) {
  const { t } = useTranslation('core')
  return (
    <Pill tone={DIRECTION_PILL_TONE[direction]}>
      {t(`clients.packages.changePlan.direction.${direction.toLowerCase()}`)}
    </Pill>
  )
}

export function ChangePlanDialog({
  clientId,
  siteId,
  serviceId,
  serviceName,
  currentPrice,
  currency,
  nextInvoiceDate,
  siteSessionTypeId,
  onChangePlan,
}: ChangePlanDialogProps) {
  const { t, i18n } = useTranslation('core')
  const locale = i18n.language
  const { closeDialog } = useDialog()
  const { openToast } = useToast()
  const restoredSnapshot = _snapshot?.serviceId === serviceId ? _snapshot : null
  const [step, setStep] = useState<Step>(() => restoredSnapshot?.step ?? 1)
  const [selectedPlan, setSelectedPlan] = useState<PlanOption | null>(
    () => restoredSnapshot?.selectedPlan ?? null
  )
  const [effectiveType, setEffectiveType] = useState<EffectiveType>(
    () => restoredSnapshot?.effectiveType ?? 'EndOfCycle'
  )
  const [reason, setReason] = useState<string>(() => restoredSnapshot?.reason ?? '')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [signedClientAgreementId, setSignedClientAgreementId] = useState<string | null>(
    () => restoredSnapshot?.signedClientAgreementId ?? null
  )

  // Clear snapshot after consuming it on mount
  useEffect(() => {
    if (_snapshot?.serviceId === serviceId) {
      _snapshot = null
    }
  }, [])

  const {
    planOptions,
    isLoading,
    error: planOptionsError,
  } = useAvailablePlanOptions(siteId, serviceId, siteSessionTypeId)

  useEffect(() => {
    if (planOptionsError) {
      openToast({
        title: t('clients.packages.errors.loadPlanOptionsFailed'),
        severity: 'error',
      })
    }
  }, [planOptionsError])

  const { proratedTotalAmount, isLoadingProration } = useCalculateCart({
    siteId,
    serviceId,
    selectedPlan,
    effectiveType,
  })

  const { data: siteData } = useGetSiteById(siteId, { refetchOnRender: false })
  const { data: meData } = useMe()
  const salesperson = meData?.me
    ? `${meData.me.firstName ?? ''} ${meData.me.lastName ?? ''}`.trim() || null
    : null

  const { agreementReq } = useChangePlanAgreement(siteId, selectedPlan?.serviceTemplateId)

  const hasAgreement =
    !!agreementReq?.agreementEnabled &&
    !!(agreementReq.siteAgreementTemplateId || agreementReq.agreementText)

  const isDowngrade = selectedPlan?.direction === 'DOWNGRADE'
  const requiresReason = effectiveType === 'Immediate' && isDowngrade
  const isReasonMissing = requiresReason && !reason.trim()

  const handleSelectPlan = (plan: PlanOption) => {
    setSelectedPlan(plan)
    setEffectiveType(plan.direction === 'UPGRADE' ? 'Immediate' : 'EndOfCycle')
    setReason('')
    setSignedClientAgreementId(null)
  }

  const handleNext = () => {
    if (step === 1 && selectedPlan) {
      setStep(2)
    } else if (step === 2 && !isReasonMissing) {
      setStep(hasAgreement ? 3 : 4)
    } else if (step === 3 && signedClientAgreementId) {
      setStep(4)
    }
  }

  const handleBack = () => {
    if (step === 2) {
      setStep(1)
    } else if (step === 3) {
      setStep(2)
    } else if (step === 4) {
      setStep(hasAgreement ? 3 : 2)
    }
  }

  const handleConfirm = async () => {
    if (!selectedPlan || isReasonMissing) return
    setIsSubmitting(true)
    try {
      await onChangePlan({
        toServiceTemplateId: selectedPlan.serviceTemplateId,
        effectiveType,
        reason: reason || undefined,
        signedClientAgreementId: signedClientAgreementId || undefined,
      })
      closeDialog()
    } finally {
      setIsSubmitting(false)
    }
  }

  const stepTitle = t(`clients.packages.changePlan.step${step}.title`)

  const getPrimaryAction = (): {
    label: string
    action: () => void
    props?: { disabled?: boolean }
    isLoading?: boolean
  } => {
    if (step === 4) {
      return {
        label: t('clients.packages.changePlan.confirm'),
        action: handleConfirm,
        isLoading: isSubmitting,
        props: { disabled: isSubmitting },
      }
    }
    if (step === 3) {
      return {
        label: t('common.next'),
        action: handleNext,
        props: { disabled: !signedClientAgreementId },
      }
    }
    return {
      label: t('common.next'),
      action: handleNext,
      props: { disabled: step === 1 ? !selectedPlan : isReasonMissing },
    }
  }
  const primaryAction = getPrimaryAction()

  const secondaryAction =
    step === 1
      ? { label: t('common.cancel'), action: closeDialog }
      : { label: t('common.goBack'), action: handleBack }

  return (
    <DialogLayout
      title={stepTitle}
      size={SIZES.SM}
      closeType={CLOSE_TYPES.ICON}
      closeAction={closeDialog}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
    >
      <Box sx={changePlanDialogStyles.dialogContent}>
        {step === 1 && (
          <Step1SelectPlan
            serviceName={serviceName}
            planOptions={planOptions}
            isLoading={isLoading}
            error={planOptionsError}
            selectedPlan={selectedPlan}
            onSelectPlan={handleSelectPlan}
            locale={locale}
            currency={currency}
          />
        )}
        {step === 2 && selectedPlan && (
          <Step2Schedule
            selectedPlan={selectedPlan}
            effectiveType={effectiveType}
            reason={reason}
            nextInvoiceDate={nextInvoiceDate}
            onEffectiveTypeChange={setEffectiveType}
            onReasonChange={setReason}
            requiresReason={requiresReason}
            proratedTotalAmount={proratedTotalAmount}
            isLoadingProration={isLoadingProration}
            locale={locale}
            currency={currency}
          />
        )}
        {step === 3 && selectedPlan && agreementReq && (
          <Step3Agreement
            clientId={clientId}
            siteId={siteId}
            serviceId={serviceId}
            agreementReq={agreementReq}
            signedClientAgreementId={signedClientAgreementId}
            onSigned={setSignedClientAgreementId}
            onBeforeOpenSignDialog={() => {
              _snapshot = {
                serviceId,
                step,
                selectedPlan,
                effectiveType,
                reason,
                signedClientAgreementId,
              }
            }}
            locale={locale}
            currency={currency}
            nextInvoiceDate={nextInvoiceDate}
            siteData={siteData}
            effectiveType={effectiveType}
            salesperson={salesperson}
          />
        )}
        {step === 4 && selectedPlan && (
          <Step4Confirm
            serviceName={serviceName}
            currentPrice={currentPrice}
            selectedPlan={selectedPlan}
            effectiveType={effectiveType}
            nextInvoiceDate={nextInvoiceDate}
            proratedTotalAmount={proratedTotalAmount}
            locale={locale}
            currency={currency}
          />
        )}
      </Box>
    </DialogLayout>
  )
}

interface Step1Props {
  serviceName: string
  planOptions: PlanOption[]
  isLoading: boolean
  error: Error | null
  selectedPlan: PlanOption | null
  onSelectPlan: (plan: PlanOption) => void
  locale: string
  currency: string
}

function Step1SelectPlan({
  serviceName,
  planOptions,
  isLoading,
  error,
  selectedPlan,
  onSelectPlan,
  locale,
  currency,
}: Step1Props) {
  const { t } = useTranslation('core')

  return (
    <Box sx={changePlanDialogStyles.stepContent}>
      <Typography variant="body2" color="text.secondary">
        {serviceName}
      </Typography>
      <Typography variant="subtitle2">
        {t('clients.packages.changePlan.step1.selectPlan')}
      </Typography>
      {isLoading && (
        <>
          <Skeleton variant="rounded" height={64} />
          <Skeleton variant="rounded" height={64} />
        </>
      )}
      {!isLoading && error && (
        <Typography color="error" variant="body2">
          {t('clients.packages.errors.loadPlanOptionsFailed')}
        </Typography>
      )}
      {!isLoading && !error && planOptions.length === 0 && (
        <Typography color="text.secondary" variant="body2">
          {t('clients.packages.changePlan.step1.noPlansAvailable')}
        </Typography>
      )}
      {!isLoading &&
        !error &&
        planOptions.map((plan) => {
          const isSelected = selectedPlan?.serviceTemplateId === plan.serviceTemplateId
          const priceDelta = Number(plan.priceDelta)
          return (
            <Box
              key={plan.serviceTemplateId}
              onClick={() => onSelectPlan(plan)}
              sx={getPlanOptionCardSx(isSelected)}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {plan.name}
                  <DirectionBadge direction={plan.direction} />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatCurrency(plan.price, locale, currency)}{' '}
                  {priceDelta !== 0 && (
                    <Box component="span" sx={getPriceDeltaSpanSx(priceDelta > 0)}>
                      ({priceDelta > 0 ? '+' : ''}
                      {formatCurrency(plan.priceDelta, locale, currency)})
                    </Box>
                  )}
                </Typography>
              </Box>
            </Box>
          )
        })}
    </Box>
  )
}

interface Step2Props {
  selectedPlan: PlanOption
  effectiveType: EffectiveType
  reason: string
  nextInvoiceDate?: string
  onEffectiveTypeChange: (value: EffectiveType) => void
  onReasonChange: (value: string) => void
  requiresReason: boolean
  proratedTotalAmount: string | null
  isLoadingProration: boolean
  locale: string
  currency: string
}

function Step2Schedule({
  selectedPlan,
  effectiveType,
  reason,
  nextInvoiceDate,
  onEffectiveTypeChange,
  onReasonChange,
  requiresReason,
  proratedTotalAmount,
  isLoadingProration,
  locale,
  currency,
}: Step2Props) {
  const { t } = useTranslation('core')
  const isDowngrade = selectedPlan.direction === 'DOWNGRADE'

  const immediateLabel = (
    <Box>
      <Typography variant="body2" fontWeight={600}>
        {t('clients.packages.changePlan.step2.immediately')}
        {!isDowngrade && (
          <Pill tone="INFO">{t('clients.packages.changePlan.step2.recommended')}</Pill>
        )}
        {isDowngrade && (
          <Pill tone="WARNING">{t('clients.packages.changePlan.step2.staffOverride')}</Pill>
        )}
      </Typography>
      {!isDowngrade && (
        <Typography variant="caption" color="text.secondary">
          {t('clients.packages.changePlan.step2.immediateInfo')}
        </Typography>
      )}
    </Box>
  )

  const endOfCycleLabel = (
    <Box>
      <Typography variant="body2" fontWeight={600}>
        {t('clients.packages.changePlan.step2.endOfCycle')}
        {isDowngrade && (
          <Pill tone="INFO">{t('clients.packages.changePlan.step2.recommended')}</Pill>
        )}
      </Typography>
      {nextInvoiceDate && (
        <Typography variant="caption" color="text.secondary">
          {t('clients.packages.changePlan.step2.effectiveDate', {
            date: formatDate(nextInvoiceDate, locale),
          })}
        </Typography>
      )}
    </Box>
  )

  return (
    <Box sx={changePlanDialogStyles.stepContent}>
      <Typography variant="subtitle2">{t('clients.packages.changePlan.step2.when')}</Typography>
      <FormControl component="fieldset">
        <RadioGroup
          value={effectiveType}
          onChange={(e) => onEffectiveTypeChange(e.target.value as EffectiveType)}
        >
          <FormControlLabel value="Immediate" control={<Radio />} label={immediateLabel} />
          <FormControlLabel value="EndOfCycle" control={<Radio />} label={endOfCycleLabel} />
        </RadioGroup>
      </FormControl>

      {effectiveType === 'Immediate' && !isDowngrade && (
        <>
          {isLoadingProration && <Skeleton variant="rounded" height={48} />}
          {!isLoadingProration && proratedTotalAmount && (
            <Alert severity="warning" sx={changePlanDialogStyles.alert}>
              {t('clients.packages.changePlan.step2.proratedAmount', {
                amount: formatCurrency(proratedTotalAmount, locale, currency),
              })}
            </Alert>
          )}
          {!isLoadingProration && !proratedTotalAmount && (
            <Alert severity="info" sx={changePlanDialogStyles.alert}>
              {t('clients.packages.changePlan.step2.proratedNotice')}
            </Alert>
          )}
        </>
      )}

      {requiresReason && (
        <TextField
          label={t('clients.packages.changePlan.step2.reasonLabel')}
          value={reason}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            onReasonChange(e.target.value)
          }
          multiline
          rows={3}
          fullWidth
          required
          helperText={t('clients.packages.changePlan.step2.reasonHelperText')}
        />
      )}
    </Box>
  )
}

interface Step3AgreementProps {
  clientId: string
  siteId: string
  serviceId: string
  agreementReq: AgreementRequirement
  signedClientAgreementId: string | null
  onSigned: (agreementId: string) => void
  onBeforeOpenSignDialog: () => void
  locale: string
  currency: string
  nextInvoiceDate?: string
  siteData?: SiteShortcodeData | null
  effectiveType: 'Immediate' | 'EndOfCycle'
  salesperson?: string | null
}

function Step3Agreement({
  clientId,
  siteId,
  serviceId,
  agreementReq,
  signedClientAgreementId,
  onSigned: _onSigned,
  onBeforeOpenSignDialog,
  locale,
  currency,
  nextInvoiceDate,
  siteData,
  effectiveType,
  salesperson,
}: Step3AgreementProps) {
  const { t } = useTranslation('core')
  const { pushDialog } = useDialog()
  const [isAssigning, setIsAssigning] = useState(false)
  const resetSignatures = useSignatureCaptureStore((state) => state.resetSignatures)

  const [signClientAgreementMutation] = useMutation((mutation: Mutation, args) => {
    const result = mutation.signClientAgreement(args)
    void result?.agreementId
    void result?.status
    void result?.signedAt
    return result
  })

  const [createAndSignAgreementMutation] = useMutation((mutation: Mutation, args) => {
    const result = mutation.createAndSignAgreement(args)
    void result?.agreementId
    void result?.status
    void result?.signedAt
    return result
  })

  const handleSign = async () => {
    const agreementName = agreementReq.name ?? t('clients.packages.changePlan.step3.agreementName')

    // onSignaturesCollected fires while this component is unmounted (dialog stack replaces
    // ChangePlanDialog with SignAgreementDialog). We write the signed ID into the snapshot
    // so ChangePlanDialog restores it when it remounts after popDialog.
    const onSignaturesCollected = (_signatures: string[], resultAgreementId?: string) => {
      if (resultAgreementId && _snapshot?.serviceId === serviceId) {
        _snapshot.signedClientAgreementId = resultAgreementId
      }
    }

    resetSignatures()

    if (agreementReq.siteAgreementTemplateId) {
      setIsAssigning(true)
      try {
        const result = await resolve(({ mutation }) => {
          const res = mutation.assignAgreementToClient({
            clientId,
            siteId,
            siteAgreementTemplateId: agreementReq.siteAgreementTemplateId!,
          })
          return { agreementId: res?.agreementId }
        })
        if (result?.agreementId) {
          onBeforeOpenSignDialog()
          pushDialog(SignAgreementDialog, {
            agreementId: result.agreementId,
            agreementName,
            clientId,
            siteId,
            onSign: signClientAgreementMutation,
            onSignaturesCollected,
            additionalShortcodes: computePackageShortcodes(agreementReq, {
              t,
              locale,
              currency,
              nextInvoiceDate,
              siteData,
              effectiveType,
              salesperson,
            }),
          })
        }
      } finally {
        setIsAssigning(false)
      }
    } else if (agreementReq.agreementText) {
      const agreementContent = renderAgreementText(agreementReq.agreementText, {
        agreementReq,
        t,
        locale,
        currency,
        nextInvoiceDate,
        siteData,
        effectiveType,
        salesperson,
      })
      onBeforeOpenSignDialog()
      pushDialog(SignAgreementDialog, {
        agreementName,
        clientId,
        siteId,
        agreementContent,
        onCreateAndSign: createAndSignAgreementMutation,
        onSignaturesCollected,
      })
    }
  }

  return (
    <Box sx={changePlanDialogStyles.stepContent}>
      <Typography variant="body2" color="text.secondary">
        {t('clients.packages.changePlan.step3.description')}
      </Typography>
      <Box sx={changePlanDialogStyles.agreementItem}>
        <Typography variant="body2" fontWeight={600}>
          {agreementReq.name}
        </Typography>
        {signedClientAgreementId ? (
          <Box sx={changePlanDialogStyles.agreementSignedBadge}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2" color="success.main">
              {t('clients.packages.changePlan.step3.signed')}
            </Typography>
            <SharedButton size="small" variant="text" onClick={handleSign} disabled={isAssigning}>
              {t('clients.packages.changePlan.step3.reSign')}
            </SharedButton>
          </Box>
        ) : (
          <SharedButton variant="outlined" onClick={handleSign} isLoading={isAssigning}>
            {t('clients.packages.changePlan.step3.signButton')}
          </SharedButton>
        )}
      </Box>
    </Box>
  )
}

interface Step4Props {
  serviceName: string
  currentPrice: number
  selectedPlan: PlanOption
  effectiveType: EffectiveType
  nextInvoiceDate?: string
  proratedTotalAmount: string | null
  locale: string
  currency: string
}

function Step4Confirm({
  serviceName,
  currentPrice,
  selectedPlan,
  effectiveType,
  nextInvoiceDate,
  proratedTotalAmount,
  locale,
  currency,
}: Step4Props) {
  const { t } = useTranslation('core')

  const effectiveDateLabel =
    effectiveType === 'Immediate'
      ? t('clients.packages.changePlan.step4.today')
      : nextInvoiceDate
        ? formatDate(nextInvoiceDate, locale)
        : t('clients.packages.changePlan.step4.nextBillingDate')

  return (
    <Box sx={changePlanDialogStyles.stepContent}>
      <Typography variant="body2" color="text.secondary">
        {t('clients.packages.changePlan.step4.reviewBeforeConfirm')}
      </Typography>

      <Box sx={changePlanDialogStyles.confirmRowContainer}>
        <ConfirmRow label={t('clients.packages.changePlan.step4.membership')} value={serviceName} />
        <Divider />
        <ConfirmRow
          label={t('clients.packages.changePlan.step4.currentPrice')}
          value={formatCurrency(currentPrice, locale, currency)}
        />
        <Divider />
        <ConfirmRow
          label={t('clients.packages.changePlan.step4.changingTo')}
          value={
            <Box sx={changePlanDialogStyles.changingToValue}>
              <SwapHorizIcon sx={changePlanDialogStyles.swapIcon} />
              <Typography variant="body2">{selectedPlan.name}</Typography>
            </Box>
          }
        />
        <Divider />
        <ConfirmRow
          label={t('clients.packages.changePlan.step4.newPrice')}
          value={`${formatCurrency(selectedPlan.price, locale, currency)} / ${t('clients.packages.changePlan.step4.perCycle')}`}
        />
        <Divider />
        <ConfirmRow
          label={t('clients.packages.changePlan.step4.effectiveDate')}
          value={effectiveDateLabel}
        />
        {effectiveType === 'Immediate' && proratedTotalAmount && (
          <>
            <Divider />
            <ConfirmRow
              label={t('clients.packages.changePlan.step4.dueToday')}
              value={
                <Typography variant="body2" fontWeight={600} color="warning.dark">
                  {formatCurrency(proratedTotalAmount, locale, currency)}
                </Typography>
              }
            />
          </>
        )}
      </Box>

      {effectiveType === 'Immediate' && (
        <Alert severity="warning" sx={changePlanDialogStyles.alert}>
          {t('clients.packages.changePlan.step4.immediateWarning')}
        </Alert>
      )}
    </Box>
  )
}

interface ConfirmRowProps {
  label: string
  value: string | React.ReactNode
}

function ConfirmRow({ label, value }: ConfirmRowProps) {
  return (
    <Box sx={changePlanDialogStyles.confirmRow}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography variant="body2" fontWeight={600}>
          {value}
        </Typography>
      ) : (
        value
      )}
    </Box>
  )
}
